import type { LocationQualityType } from "../locationSlice";

import type { LocationQualityThreslholds } from "@/features/config/types";
import { LocationStatusTypes } from "@/features/utils/types";
import { radiansToDegrees } from "@/libs/geo";
import { type Building, Location } from "@/libs/models";

const LOCATION_BUFFER_SIZE = 10;

export const angleTo360Range = (angle: number): number => {
  angle = angle % 360;
  if (angle < 0) angle += 360;
  angle = Math.abs(angle);
  return angle;
};

export const rotation = (from: number, to: number) => {
  from = angleTo360Range(from);
  to = angleTo360Range(to);

  const unsafeRotation = to - from;
  let safeRotation = unsafeRotation % 360;
  if (safeRotation > 180) safeRotation = safeRotation - 360;
  if (safeRotation < -180) safeRotation = 360 + safeRotation;

  return safeRotation;
};

export interface ExternalLocation {
  buildingIdentifier: number;
  floorIdentifier: number;
  coordinate: {
    latitude: number;
    longitude: number;
  };
  bearing?: {
    radians: number;
    radiansMinusPiPi: number;
    degrees: number;
    degreesClockwise: number;
  };
  accuracy?: number;
}

export function processLocationQuality(
  locationQuality: LocationQualityType,
  currentLocation: Location,
  thresholds: LocationQualityThreslholds,
) {
  let avgLocAccuracy = locationQuality.avgLocAccuracy;
  let distanceWalked = locationQuality.distanceWalked;
  let biggestJump = locationQuality.biggestJump;
  let countNoHasBearings = locationQuality.countNoHasBearings;
  let userNeedsToWalk = locationQuality.userNeedsToWalk;
  let sdkLocations: Location[] = [];

  const { locationBuffer } = locationQuality;
  sdkLocations = [...locationBuffer];
  currentLocation && sdkLocations.push(currentLocation);

  if (sdkLocations.length > LOCATION_BUFFER_SIZE) {
    sdkLocations.shift();
  }

  let converged = false;
  let hasWalked = false;

  if (sdkLocations.length === LOCATION_BUFFER_SIZE) {
    const {
      convergedResult,
      avgLocAccuracyResult,
      countNoHasBearingsResult,
      enoughARMovement,
      distanceWalkedResult,
      biggestJumpResult,
    } = enoughARQuality(
      sdkLocations,
      locationQuality.avgLocAccuracy,
      locationQuality.countNoHasBearings,
      locationQuality.biggestJump,
      thresholds,
    );
    converged = convergedResult;
    avgLocAccuracy = avgLocAccuracyResult;
    countNoHasBearings = countNoHasBearingsResult;

    hasWalked = enoughARMovement;
    distanceWalked = distanceWalkedResult;
    biggestJump = biggestJumpResult;
  }
  if (!converged) {
    userNeedsToWalk = true;
  }

  let goodARQuality = converged;

  if (userNeedsToWalk) {
    goodARQuality = converged && hasWalked;
    if (goodARQuality) {
      // No need to walk if converged and already walked
      userNeedsToWalk = false;
    }
  }

  const newQuality: LocationQualityType = {
    avgLocAccuracy,
    biggestJump,
    converged,
    countNoHasBearings,
    distanceWalked,
    goodARQuality,
    hasWalked,
    locationBuffer: sdkLocations,
    userNeedsToWalk,
  };

  return newQuality;
}

function enoughARQuality(
  locations: Location[],
  avgLocAccuracy: number,
  countNoHasBearings: number,
  biggestJump: number,
  thresholds: LocationQualityThreslholds,
) {
  if (locations.length === 0)
    return {
      avgLocAccuracyResult: avgLocAccuracy,
      biggestJumpResult: biggestJump,
      convergedResult: false,
      countNoHasBearingsResult: countNoHasBearings,
      distanceWalkedResult: 0,
      enoughARMovement: false,
    };
  avgLocAccuracy = 0;
  countNoHasBearings = 0;
  let accumulatedDistance = 0.0;
  biggestJump = 0;
  for (let i = 0; i < locations.length - 1; i++) {
    const location = locations[i];
    avgLocAccuracy += location.accuracy;
    if (!location.hasBearing) countNoHasBearings += 1;

    const stepDistance = euclideanDistance(location, locations[i + 1]);
    biggestJump =
      stepDistance != null ? Math.max(stepDistance, biggestJump) : biggestJump;
    if (stepDistance && stepDistance < thresholds.maxJumpAllowedThreshold) {
      accumulatedDistance += stepDistance;
    }
  }
  avgLocAccuracy /= locations.length;

  return {
    avgLocAccuracyResult: avgLocAccuracy,
    biggestJumpResult: biggestJump,
    convergedResult:
      avgLocAccuracy < thresholds.accuracyThreshold &&
      countNoHasBearings < thresholds.locationsWithoutBearingThreshold &&
      biggestJump <= thresholds.maxJumpAllowedThreshold,
    countNoHasBearingsResult: countNoHasBearings,
    distanceWalkedResult: accumulatedDistance,
    enoughARMovement:
      accumulatedDistance < thresholds.minimumWalkThreshold ||
      biggestJump > thresholds.maxJumpAllowedThreshold
        ? false
        : true,
  };
}

function euclideanDistance(
  origin: Location,
  destination: Location,
): number | null {
  if (!origin.x || !origin.y || !destination.x || !destination.y) return null;
  const dx = (destination?.x - origin?.x) ** 2;
  const dy = (destination?.y - origin?.y) ** 2;
  return Math.sqrt(dx + dy);
}

interface CoreOutdoorLocation {
  latitude: number;
  longitude: number;
  accuracy: number;
  bearing: number;
}

interface CorePose {
  x: number;
  y: number;
  yaw: number;
}

interface CorePositioning {
  level: number;
  building: number;
  pose: CorePose;
  converged: boolean;
  radius: number;
  yawConverged: boolean;
  isGpsPose: boolean;
}

export interface CoreResponse {
  mac: string;
  timestamp: string;
  identifier: string;
  debugBuilding: string;
  positioning?: CorePositioning;
  outdoorLocation?: CoreOutdoorLocation;
}

export const coreResponseToLocation = (
  response: CoreResponse,
  building: Building,
): Location => {
  if (response?.positioning) {
    const coordinate = building?.coordinateConverter.toCoordinate({
      x: response.positioning?.pose?.x || 0,
      y: response.positioning?.pose?.y || 0,
    });

    const isBearingConverged =
      response.positioning?.yawConverged &&
      response.positioning?.pose?.yaw != null;

    const position = new Location({
      accuracy: response.positioning?.radius || 0,
      bearing: isBearingConverged
        ? radiansToDegrees(
            response.positioning.pose.yaw -
              Math.PI / 2 -
              building.coordinateConverter.rotation,
          )
        : 0,
      bearingCartesian: isBearingConverged
        ? radiansToDegrees(response.positioning.pose.yaw)
        : 0,
      buildingId: response.positioning?.building
        ? Number(response.positioning?.building)
        : Number(response.debugBuilding),
      floorId: Number(response.positioning?.level),
      hasBearing:
        response.positioning?.yawConverged &&
        response.positioning?.pose?.yaw != null,
      // TODO ALBA: add distinction between indoor and outdoor locations
      isIndoor: true,
      isOutdoor: false,
      isSimulated: true,
      latitude: coordinate?.lat || 0,
      longitude: coordinate?.lng || 0,
      // TODO ALBA: map status correctly
      status: LocationStatusTypes.POSITIONING,
      timestamp: response.timestamp || 0,
      x: response.positioning?.pose.x || 0,
      y: response.positioning?.pose.y || 0,
    });

    return position;
  } else {
    const position = new Location({
      accuracy: 0,
      bearing: 0,
      buildingId: Number(response.debugBuilding ?? "-1"),
      floorId: null,
      hasBearing: false,
      isIndoor: false,
      isOutdoor: false,
      isSimulated: true,
      latitude: 0,
      longitude: 0,
      status: LocationStatusTypes.UNKNOWN,
      timestamp: response?.timestamp ?? 0,
    });
    return position;
  }

  // TODO: handle outdoor, out of building, etc
};
